import { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, Warning } from '@mui/icons-material';
import { KeyRound, Copy, Upload } from 'lucide-react';
import type { CompanyUser } from '@/lib/types/mise/company';
import { BulkCreateUserDialog } from './BulkCreateUserDialog';
import { CreateUserDialog } from './CreateUserDialog';
import { EditUserDialog } from './EditUserDialog';
import { HubAccessBulkEnableButton } from './HubAccessBulkEnableButton';
import { UserComparisonCell } from './UserComparisonCell';
import { useDeleteUser, useResetPassword, useToggleHubAccess } from '@/lib/query/hooks/useUsers';

interface CompanyUserListProps {
  users: CompanyUser[];
  isLoading?: boolean;
  error?: Error | null;
  companyId: string;
}

export function CompanyUserList({ users, isLoading, error, companyId }: CompanyUserListProps) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [bulkCreateDialogOpen, setBulkCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<CompanyUser | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<CompanyUser | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState<CompanyUser | null>(null);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resetPasswordResultDialogOpen, setResetPasswordResultDialogOpen] = useState(false);
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const deleteUser = useDeleteUser(companyId);
  const resetPassword = useResetPassword(companyId);
  const toggleHubAccess = useToggleHubAccess(companyId);
  // `${email}:${account_id}` 形式で切替中の (user, account) の組を保持する
  const [pendingHubToggleKey, setPendingHubToggleKey] = useState<string | null>(null);
  const [hubToggleError, setHubToggleError] = useState<string | null>(null);

  const handleToggleHubAccess = async (
    user: CompanyUser,
    accountId: string,
    nextEnabled: boolean,
  ) => {
    setHubToggleError(null);
    const key = `${user.user_email}:${accountId}`;
    setPendingHubToggleKey(key);
    try {
      await toggleHubAccess.mutateAsync({
        email: user.user_email,
        accountId,
        enabled: nextEnabled,
      });
    } catch (error) {
      console.error('StoreHub アクセス切替に失敗しました:', error);
      setHubToggleError(
        error instanceof Error
          ? `${user.user_email} (${accountId}): ${error.message}`
          : 'StoreHub アクセス切替に失敗しました',
      );
    } finally {
      setPendingHubToggleKey(null);
    }
  };

  const handleOpenDeleteDialog = (user: CompanyUser) => {
    setDeleteError(null);
    setDeletingUser(user);
    setConfirmEmail('');
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (!deleteUser.isPending) {
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      setDeleteError(null);
      setConfirmEmail('');
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser) return;
    setDeleteError(null);

    try {
      await deleteUser.mutateAsync(deletingUser.user_id);
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      setConfirmEmail('');
    } catch (error) {
      console.error('ユーザーの削除に失敗しました:', error);
      setDeleteError(error instanceof Error ? error.message : 'ユーザーの削除に失敗しました。');
    }
  };

  // メールアドレスは大文字小文字を区別しない仕様であるため、確認比較も case-insensitive に揃える
  const isDeleteEmailConfirmed = deletingUser
    ? confirmEmail.trim().toLowerCase() === deletingUser.user_email.trim().toLowerCase()
    : false;

  const handleOpenResetPasswordDialog = (user: CompanyUser) => {
    setResetPasswordError(null);
    setResetPasswordUser(user);
    setResetPasswordDialogOpen(true);
  };

  const handleCloseResetPasswordDialog = () => {
    if (!resetPassword.isPending) {
      setResetPasswordDialogOpen(false);
      setResetPasswordUser(null);
      setResetPasswordError(null);
    }
  };

  const handleConfirmResetPassword = async () => {
    if (!resetPasswordUser) return;
    setResetPasswordError(null);

    try {
      const result = await resetPassword.mutateAsync(resetPasswordUser.user_id);
      setTemporaryPassword(result.temporary_password);
      setResetPasswordDialogOpen(false);
      setResetPasswordResultDialogOpen(true);
    } catch (error) {
      console.error('パスワードの再発行に失敗しました:', error);
      setResetPasswordError(
        error instanceof Error ? error.message : 'パスワードの再発行に失敗しました。',
      );
    }
  };

  const handleCloseResetPasswordResultDialog = () => {
    setResetPasswordResultDialogOpen(false);
    setResetPasswordUser(null);
    setTemporaryPassword('');
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    await navigator.clipboard.writeText(temporaryPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'ユーザー一覧の取得に失敗しました。'}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <HubAccessBulkEnableButton users={users} companyId={companyId} />
        <Button
          variant="outlined"
          startIcon={<Upload size={16} />}
          onClick={() => setBulkCreateDialogOpen(true)}
        >
          CSVで一括登録
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
          ユーザーを追加
        </Button>
      </Box>
      {hubToggleError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setHubToggleError(null)}>
          {hubToggleError}
        </Alert>
      )}
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer
          sx={{
            overflowX: 'auto',
            maxWidth: '100%',
            '& .MuiTable-root': {
              minWidth: 650,
            },
          }}
        >
          <Table sx={{ minWidth: 650, whiteSpace: 'nowrap' }} aria-label="ユーザーテーブル">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>ユーザー名</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>メールアドレス</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>店舗</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>比較店舗</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>アクセス可能なアカウント</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>デフォルトアカウント</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  StoreHub アクセス可能なアカウント
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">ユーザーが見つかりませんでした</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user, index) => (
                  <TableRow key={`${user.user_id}-${index}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {user.user_name || '未設定'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.user_email}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {user.app_settings?.seller_name ?? user.app_settings?.seller_id ?? (
                        <Typography color="text.secondary">未設定</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {user.default_account_id && user.app_settings ? (
                        <UserComparisonCell
                          accountId={user.default_account_id}
                          settings={user.app_settings}
                        />
                      ) : (
                        <Typography color="text.secondary">未設定</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {user.account_ids.length === 0 ? (
                        <Typography color="text.secondary">なし</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {user.account_ids.map((accountId) => (
                            <Chip key={accountId} label={accountId} size="small" />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {user.default_account_id ? (
                        <Chip label={user.default_account_id} size="small" color="primary" />
                      ) : (
                        <Typography color="text.secondary">未設定</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.account_ids.length === 0 ? (
                        <Typography color="text.secondary">なし</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {user.account_ids.map((accountId) => {
                            const enabled = user.hub_access_enabled_account_ids.includes(accountId);
                            const key = `${user.user_email}:${accountId}`;
                            return (
                              <Box
                                key={accountId}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                              >
                                <Tooltip
                                  title={
                                    enabled
                                      ? `${accountId}: StoreHub ログイン可（OFF で即時ブロック）`
                                      : `${accountId}: StoreHub ログイン不可（ON で許可）`
                                  }
                                >
                                  <Switch
                                    size="small"
                                    checked={enabled}
                                    onChange={(e) =>
                                      handleToggleHubAccess(user, accountId, e.target.checked)
                                    }
                                    disabled={pendingHubToggleKey === key}
                                    slotProps={{
                                      input: {
                                        'aria-label': `${user.user_email} の account ${accountId} に対する StoreHub アクセス切替`,
                                      },
                                    }}
                                  />
                                </Tooltip>
                                <Typography variant="body2">{accountId}</Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingUser(user);
                          setEditDialogOpen(true);
                        }}
                        aria-label="ユーザーを編集"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenResetPasswordDialog(user)}
                        aria-label="パスワードを再発行"
                      >
                        <KeyRound size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleOpenDeleteDialog(user)}
                        aria-label="ユーザーを削除"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        companyId={companyId}
      />

      <BulkCreateUserDialog
        open={bulkCreateDialogOpen}
        onClose={() => setBulkCreateDialogOpen(false)}
        companyId={companyId}
      />

      {editingUser && (
        <EditUserDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setEditingUser(null);
          }}
          companyId={companyId}
          user={editingUser}
        />
      )}

      {/* ユーザー削除確認ダイアログ */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <Warning color="error" />
          ユーザーの削除確認
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            以下のユーザーを削除します。
          </Typography>
          {deletingUser && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                ユーザー名
              </Typography>
              <Typography variant="body1">{deletingUser.user_name || '未設定'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                メールアドレス
              </Typography>
              <Typography variant="body1">{deletingUser.user_email}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            この操作は元に戻せません。ユーザーのCognitoアカウントも削除されます。
          </Alert>
          <TextField
            fullWidth
            size="small"
            label="確認のため、対象ユーザーのメールアドレスを入力してください"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder={deletingUser?.user_email ?? ''}
            disabled={deleteUser.isPending}
            sx={{ mt: 2 }}
            autoComplete="off"
          />
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleteUser.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleteUser.isPending || !isDeleteEmailConfirmed}
            startIcon={deleteUser.isPending ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleteUser.isPending ? '削除中...' : '削除する'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* パスワード再発行確認ダイアログ */}
      <Dialog
        open={resetPasswordDialogOpen}
        onClose={handleCloseResetPasswordDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>パスワードを再発行しますか？</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            以下のユーザーのパスワードを再発行します。新しい仮パスワードが画面に表示されます。
          </Typography>
          {resetPasswordUser && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                ユーザー名
              </Typography>
              <Typography variant="body1">{resetPasswordUser.user_name || '未設定'}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                メールアドレス
              </Typography>
              <Typography variant="body1">{resetPasswordUser.user_email}</Typography>
            </Box>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            初回ログイン時に新しいパスワードへの変更が必要です。
          </Alert>
          {resetPasswordError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {resetPasswordError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetPasswordDialog} disabled={resetPassword.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmResetPassword}
            variant="contained"
            disabled={resetPassword.isPending}
          >
            {resetPassword.isPending ? '再発行中...' : '再発行する'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 仮パスワード表示ダイアログ */}
      <Dialog open={resetPasswordResultDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>パスワードを再発行しました</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <code
                style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  fontSize: '1.1rem',
                  fontFamily: 'monospace',
                  flex: 1,
                  wordBreak: 'break-all',
                }}
              >
                {temporaryPassword}
              </code>
              <IconButton onClick={handleCopyPassword} size="small" title="コピー">
                <Copy size={18} />
              </IconButton>
              {copied && (
                <Typography variant="body2" color="success.main">
                  コピーしました
                </Typography>
              )}
            </Box>
            <Alert severity="warning">このダイアログを閉じると仮パスワードは再表示できません</Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetPasswordResultDialog} variant="contained">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
